import { DateTime } from 'luxon';

import { parseBECategoryToCategory } from 'constants/categories';
import { imageUrlKey } from 'constants/rulebookVariables';
import type { EventPositionVo, PositionVo } from 'models';
import {
    centsFromDollarsString,
    centsFromDollarsStringOrNull,
    centsFromDollarsStringOrZero,
    type DollarsString,
    sumExactFeesOrEstimate,
} from 'models/dollars';
import type { EventVo } from 'models/event';
import { numberFromFixedPointString } from 'models/fixedPoint';
import type { MarketVo } from 'models/market';
import type {
    PortfolioEvent,
    PortfolioEventBucket,
    PortfolioMarket,
    PortfolioMarketPosition,
    PortfolioPosition,
} from 'models/portfolioEvent';
import type { PositionDTO, TickerDTO } from 'models/ws';
import type { EventEntity, MarketEntity, SeriesEntity } from 'queries/marketEntityTransform';
import {
    isMarketClosed,
    isMarketDetermined,
    isMarketPendingPayout,
    isMarketSettled,
    marketContractDecimals,
    marketImageUrl,
    marketNoSellPrice,
    marketParsedExpectedSettlementDate,
    marketRelativeUrl,
    marketResultIsNo,
    marketResultIsScalar,
    marketResultIsYes,
    marketResultText,
    marketYesSellPrice,
} from 'queries/marketEntityTransform';
import config from 'utils/config';
import { getEventRelativeUrl } from 'utils/events';
import { getEventImageUrl, toCdnImageUrl } from 'utils/images';

/**
 * Convert one of the upstream `*_dollars` 4-decimal strings (e.g. `'0.9950'`)
 * into a cents number that preserves sub-cent precision (`99.5`). The
 * companion `*_price` integer fields are rounded to whole cents and lose
 * decicent precision on markets trading above 99¢ — using the dollar string
 * keeps the Buy/Sell pills correct on those markets.
 *
 * Falls back to the integer field when the dollar string is missing or
 * malformed (e.g. older fixture data).
 */
const dollarsStringToCents = (dollars: DollarsString | undefined, fallbackCents: number): number => {
    return centsFromDollarsString(dollars, fallbackCents);
};

export const parseAssociatedMarketTickers = (raw: string | undefined): string[] =>
    typeof raw === 'string' && raw.length > 0 ? raw.split(',') : [];

export const enrichCloseDates = (events: EventVo[]): void => {
    for (const event of events) {
        const times = event.markets
            .map((m) => m.close_date)
            .filter(Boolean)
            .map((t) => new Date(t).getTime())
            .filter((t) => !isNaN(t));
        if (times.length > 0) {
            const iso = new Date(Math.max(...times)).toISOString();
            event.close_date = iso.slice(0, 19) + 'Z';
        }
    }
};

interface EventCtxForMarket {
    ticker: string;
    series_ticker: string;
    markets: readonly unknown[];
}

/**
 * The market fields the position-derivation math actually reads. A structural
 * subset of both `PortfolioMarket` (plain snapshot) and the market-entity-
 * derived snapshot used on event pages, so either can back a derived position.
 */
export type PositionMarketSnapshot = Pick<
    PortfolioMarket,
    | 'lastPrice'
    | 'isPendingPayout'
    | 'resultIsYes'
    | 'resultIsNo'
    | 'resultIsScalar'
    | 'settlementValueCents'
    | 'isSettled'
    | 'isClosed'
    | 'isDetermined'
    | 'previousHourPrice'
    | 'previousDayPrice'
    | 'previousWeekPrice'
>;

export const marketVoToPortfolioMarket = (vo: MarketVo, eventVo: EventCtxForMarket): PortfolioMarket => {
    const rulebookVariables = vo.rulebook_variables ?? {};
    // Source price fields off the `*_dollars` 4-decimal strings so decicent
    // markets trading above 99¢ (e.g. yes_ask = 99.5¢) keep their precision —
    // the integer `yes_ask` field rounds those to 100, which trips
    // PricePill's `isValidPrice(100) === false` guard and blanks the pill.
    const yesBid = dollarsStringToCents(vo.yes_bid_dollars, vo.yes_bid ?? 0);
    const yesAsk = dollarsStringToCents(vo.yes_ask_dollars, vo.yes_ask ?? 0);
    const lastPrice = dollarsStringToCents(vo.last_price_dollars, vo.last_price ?? 0);
    const noAsk = 100 - yesBid;
    const result = vo.result ?? '';
    const resultIsYes = result === 'yes';
    const resultIsNo = result === 'no';
    const resultIsScalar = result === 'scalar';
    const settlementValueCents = (vo.settlement_value_centicents ?? 0) / 100;
    const fractionalTradingEnabled = vo.fractional_trading_enabled ?? false;

    const isSettled = vo.status === 'settled' || vo.status === 'finalized';
    const isDetermined =
        vo.status === 'determined' || vo.status === 'disputed' || vo.status === 'amended' || isSettled || !!result;
    const closeDate = vo.close_date ?? '';
    const isClosed =
        vo.status === 'closed' || isDetermined || (!!closeDate && DateTime.fromISO(closeDate) < DateTime.now());

    const resultText = !result
        ? 'Pending'
        : resultIsYes
          ? 'Yes'
          : resultIsNo
            ? 'No'
            : resultIsScalar && settlementValueCents
              ? `${settlementValueCents}%`
              : '';

    const resultColor = !result
        ? 'text-text-x10'
        : resultIsYes
          ? 'text-yes-x10'
          : resultIsNo
            ? 'text-no-x10'
            : 'text-text-x10';

    const eventTicker = eventVo.ticker;
    const seriesTicker = eventVo.series_ticker;
    const marketTicker = vo.ticker_name;
    const relativeUrl = getEventRelativeUrl(seriesTicker, undefined, eventTicker, marketTicker);

    const imagePath = rulebookVariables[imageUrlKey];
    const imageUrl = imagePath ? toCdnImageUrl(imagePath, 'xs') : null;

    const expectedExpirationDate = vo.expected_expiration_date ?? '';
    const settlementTimerSeconds = vo.settlement_timer_seconds ?? 0;
    const parsedCloseDate = DateTime.fromISO(closeDate);
    const parsedExpectedExpirationDate = DateTime.fromISO(expectedExpirationDate);
    const effectiveDate =
        parsedCloseDate.isValid && parsedCloseDate > parsedExpectedExpirationDate
            ? parsedCloseDate
            : parsedExpectedExpirationDate;
    const parsedExpectedSettlementDate = effectiveDate.plus({ seconds: settlementTimerSeconds }).toISO() ?? '';

    // Single-market event with no scalar settlement is a "true binary" in portfolio UI terms
    const isTrueBinary = eventVo.markets.length === 1;

    return {
        id: vo.id,
        ticker: marketTicker,
        eventTicker,
        subtitle: vo.yes_sub_title || vo.sub_title || '',
        relativeUrl,
        closeDate,
        feeWaiverExpirationDate: vo.fee_waiver_expiration_date ?? null,
        parsedExpectedSettlementDate,
        imageUrl,
        parentEvent: { ticker: eventTicker, isTrueBinary },
        lastPrice,
        yesAsk,
        noAsk,
        yesSellPrice: yesBid,
        noSellPrice: 100 - yesAsk,
        // Mirror BinaryMarket.{yes,no}PriceInCents — `=== 100` (not `>= 100`)
        // so a sub-cent value like 99.5 still surfaces a price.
        yesPriceInCents: yesAsk === 100 ? null : yesAsk,
        noPriceInCents: noAsk === 100 ? null : noAsk,
        volume: (vo.volume ?? 0) / 2,
        openInterest: (vo.open_interest ?? 0) / 2,
        contractDecimals: fractionalTradingEnabled ? 2 : 0,
        fractionalTradingEnabled,
        settlementValueCents,
        rulebookVariables,
        mveSelectedLegs: vo.mve_selected_legs,
        status: vo.status,
        result,
        resultIsYes,
        resultIsNo,
        resultIsScalar,
        resultText,
        resultColor,
        isClosed,
        isDetermined,
        isSettled,
        isPendingPayout: isDetermined && !isSettled,
        isOpen: vo.status === 'active' || (vo.status === 'inactive' && !result),
        previousHourPrice: vo.previous_hour_price ?? 0,
        previousDayPrice: vo.previous_day_price ?? 0,
        previousWeekPrice: vo.previous_week_price ?? 0,
    };
};

/**
 * `MarketEntity`/`EventEntity` counterpart to {@link marketVoToPortfolioMarket} — builds the same
 * `PortfolioMarket` view-model from the entity model instead of a raw `MarketVo`. For consumers
 * (event page, single-event position views) that already hold `MarketEntity`s from
 * `marketEntitiesStore` rather than fetching a portfolio-list `MarketVo` batch — avoids adding a
 * second, duplicate market-fetch mechanism for the same data (see `eventPositionTransform.ts`).
 *
 * `MarketEntity` carries no combo-leg data (`mve_selected_legs`), so `mveSelectedLegs` is always
 * `undefined` here — fine for non-combo single-market position consumers (ShareableModal,
 * BetslipModal, YourPositionSection); combo rows should keep using the VO-based path.
 *
 * `parsedExpectedSettlementDate` uses `marketParsedExpectedSettlementDate` — the full
 * `BinaryMarket.parsedExpectedSettlementDate` mirror, which caps the expected-expiration date to
 * `expirationDate` (`vo.expiration_date`, the hard latest-allowed expiration). The VO-transform
 * counterpart computes it directly from `expected_expiration_date` alone, a simplification that
 * ignores `expiration_date` entirely. The two agree whenever `expiration_date` is present and not
 * earlier than `expected_expiration_date` (the common case); this path is the more correct one.
 */
export const marketEntityToPortfolioMarket = (
    market: MarketEntity,
    event: Pick<EventEntity, 'ticker' | 'seriesTicker'>,
    series: SeriesEntity | undefined,
    isTrueBinary: boolean,
): PortfolioMarket => ({
    id: market.id,
    ticker: market.ticker,
    eventTicker: event.ticker,
    subtitle: market.subtitle,
    relativeUrl: marketRelativeUrl(market, event, series?.title),
    closeDate: market.closeDate,
    feeWaiverExpirationDate: market.feeWaiverExpirationDate,
    parsedExpectedSettlementDate: marketParsedExpectedSettlementDate(market).toISO() ?? '',
    imageUrl: marketImageUrl(market),
    parentEvent: { ticker: event.ticker, isTrueBinary },
    lastPrice: market.lastPrice,
    yesAsk: market.yesAsk,
    noAsk: market.noAsk,
    yesSellPrice: marketYesSellPrice(market),
    noSellPrice: marketNoSellPrice(market),
    yesPriceInCents: market.yesPriceInCents,
    noPriceInCents: market.noPriceInCents,
    volume: market.volume,
    openInterest: market.openInterest,
    contractDecimals: marketContractDecimals(market),
    fractionalTradingEnabled: market.fractionalTradingEnabled,
    settlementValueCents: market.settlementValueCents,
    rulebookVariables: market.rulebookVariables,
    mveSelectedLegs: undefined,
    status: market.status,
    result: market.result,
    resultIsYes: marketResultIsYes(market),
    resultIsNo: marketResultIsNo(market),
    resultIsScalar: marketResultIsScalar(market),
    resultText: marketResultText(market),
    // Not `marketResultColor` (a `BinaryMarket.resultColor` mirror — returns an inline CSS/palette
    // value). `PortfolioMarket.resultColor` is consumed as a Tailwind class name by the
    // portfolio-list row components, matching `marketVoToPortfolioMarket`'s own local mapping.
    resultColor: !market.result
        ? 'text-text-x10'
        : marketResultIsYes(market)
          ? 'text-yes-x10'
          : marketResultIsNo(market)
            ? 'text-no-x10'
            : 'text-text-x10',
    isClosed: isMarketClosed(market),
    isDetermined: isMarketDetermined(market),
    isSettled: isMarketSettled(market),
    isPendingPayout: isMarketPendingPayout(market),
    // Not `isMarketOpen` (a stricter BinaryMarket.isOpen mirror, `status === 'active'` only) —
    // matches `marketVoToPortfolioMarket`'s own portfolio-specific "open" definition, which also
    // treats an inactive/no-result market as still open (pending activation, not yet closed).
    isOpen: market.status === 'active' || (market.status === 'inactive' && !market.result),
    previousHourPrice: market.previousHourPrice,
    previousDayPrice: market.previousDayPrice,
    previousWeekPrice: market.previousWeekPrice,
});

/**
 * Position scalars in plain-number form (cents for money fields), the shared
 * input for `derivePortfolioPosition`. Produced from a `PositionVo` via
 * `positionScalarsFromVo`, or from any other source that already carries the
 * parsed numbers (e.g. `userPositionsStore` entries).
 */
export interface PositionScalars {
    position: number;
    positionFp?: string;
    positionCost: number;
    totalCost: number;
    buyingCost: number;
    realizedPnl: number;
    feesPaid: number;
    volume: number;
    finalPosition: number;
    finalPositionCost: number;
    lastPosition: number;
    /** Exact per-leg fee costs (cents); `null` falls back to the proportional estimate. */
    yesBuyingFeeCost: number | null;
    noBuyingFeeCost: number | null;
    positionFeeCost: number | null;
    finalPositionFeeCost: number | null;
    isSettled: boolean;
    isClosed: boolean;
    lastUpdatedTs: string;
    marketId: string;
    marketTicker: string;
}

/**
 * Project a `PositionDTO` into a `PositionVo` shape so we can re-run the
 * existing VO-based transforms. The DTO doesn't carry `is_closed`; bucket
 * membership is inferred by the caller, so `is_settled` stands in for it.
 */
export const positionDtoToVo = (dto: PositionDTO): PositionVo => ({
    user_id: dto.user_id,
    market_id: dto.market_id,
    market_ticker: dto.market_ticker,
    position_fp: dto.position_fp,
    position_cost_dollars: dto.position_cost_dollars,
    total_cost_dollars: dto.total_cost_dollars,
    buying_cost_dollars: dto.buying_cost_dollars,
    realized_pnl_dollars: dto.realized_pnl_dollars,
    fees_paid_dollars: dto.fees_paid_dollars,
    yes_buying_fee_cost_dollars: dto.yes_buying_fee_cost_dollars,
    no_buying_fee_cost_dollars: dto.no_buying_fee_cost_dollars,
    position_fee_cost_dollars: dto.position_fee_cost_dollars,
    final_position_fee_cost_dollars: dto.final_position_fee_cost_dollars,
    volume_fp: dto.volume_fp,
    final_position_fp: dto.final_position_fp,
    final_position_cost_dollars: dto.final_position_cost_dollars,
    last_position_fp: dto.last_position_fp,
    is_settled: dto.is_settled,
    is_closed: dto.is_settled,
});

export const positionScalarsFromVo = (vo: PositionVo): PositionScalars => ({
    position: numberFromFixedPointString(vo.position_fp),
    positionFp: vo.position_fp,
    // Source cost / P&L / fee scalars off the sub-cent `*_dollars` strings
    // only. The legacy integer fields round fees/costs to whole cents, which
    // understates fees and overstates the resulting payout by up to a cent.
    positionCost: centsFromDollarsStringOrZero(vo.position_cost_dollars),
    totalCost: centsFromDollarsStringOrZero(vo.total_cost_dollars),
    buyingCost: centsFromDollarsStringOrZero(vo.buying_cost_dollars),
    realizedPnl: centsFromDollarsStringOrZero(vo.realized_pnl_dollars),
    feesPaid: centsFromDollarsStringOrZero(vo.fees_paid_dollars),
    volume: numberFromFixedPointString(vo.volume_fp),
    finalPosition: numberFromFixedPointString(vo.final_position_fp),
    finalPositionCost: centsFromDollarsStringOrZero(vo.final_position_cost_dollars),
    lastPosition: numberFromFixedPointString(vo.last_position_fp),
    yesBuyingFeeCost: centsFromDollarsStringOrNull(vo.yes_buying_fee_cost_dollars),
    noBuyingFeeCost: centsFromDollarsStringOrNull(vo.no_buying_fee_cost_dollars),
    positionFeeCost: centsFromDollarsStringOrNull(vo.position_fee_cost_dollars),
    finalPositionFeeCost: centsFromDollarsStringOrNull(vo.final_position_fee_cost_dollars),
    isSettled: vo.is_settled,
    isClosed: vo.is_closed,
    lastUpdatedTs: vo.last_updated_ts ?? '',
    marketId: vo.market_id,
    marketTicker: vo.market_ticker,
});

export const positionVoToPortfolioPosition = (vo: PositionVo, market: PositionMarketSnapshot): PortfolioPosition =>
    derivePortfolioPosition(positionScalarsFromVo(vo), market);

export const derivePortfolioPosition = (
    scalars: PositionScalars,
    market: PositionMarketSnapshot,
): PortfolioPosition => {
    const {
        position,
        positionCost,
        totalCost,
        buyingCost,
        realizedPnl,
        feesPaid,
        volume,
        finalPosition,
        finalPositionCost,
        lastPosition,
    } = scalars;

    const openPositionFractionOfVolume = volume === 0 ? 0 : Math.abs(position) / volume;
    const openOrFinalPositionFractionOfVolume = volume === 0 ? 0 : Math.abs(position + finalPosition) / volume;
    const openOrFinalPosition = position + finalPosition;
    const priorTradingVolume = volume - openOrFinalPosition;
    const contractsBought = priorTradingVolume / 2 + openOrFinalPosition;
    const buyingFractionOfVolume = volume === 0 ? 0 : contractsBought / volume;

    const approximateFeesPaidFromOpenPosition = Math.round(openPositionFractionOfVolume * feesPaid);
    const approximateFeesPaidFromOpenOrFinalPosition = Math.round(openOrFinalPositionFractionOfVolume * feesPaid);
    const approximateFeesPaidFromBuying = Math.round(buyingFractionOfVolume * feesPaid);
    const positionFeeCost = scalars.positionFeeCost ?? approximateFeesPaidFromOpenPosition;
    const finalPositionFeeCost = scalars.finalPositionFeeCost ?? approximateFeesPaidFromOpenOrFinalPosition;
    // Buying fees prefer the exact per-leg `*_buying_fee_cost_dollars` breakdown,
    // falling back to the proportional estimate when the payload omits it.
    const feesPaidFromBuying = sumExactFeesOrEstimate(
        scalars.yesBuyingFeeCost,
        scalars.noBuyingFeeCost,
        approximateFeesPaidFromBuying,
    );

    const exposureInDollars = positionCost / 100;
    const exposureInDollarsWithFees = (positionCost + positionFeeCost) / 100;
    const finalPositionCostInDollars = finalPositionCost / 100;
    const finalPositionCostInDollarsWithFees = (finalPositionCost + finalPositionFeeCost) / 100;
    const buyingCostInDollars = buyingCost / 100;
    const buyingCostInDollarsWithFees = (buyingCost + feesPaidFromBuying) / 100;
    const totalFeesPaidInDollars = feesPaid / 100;

    const exitEarly = position === 0 && finalPosition === 0;
    const holdsPosition = position !== 0;

    const costInDollarsWithoutFees = exitEarly
        ? buyingCostInDollars
        : holdsPosition
          ? exposureInDollars
          : finalPosition !== 0
            ? finalPositionCostInDollars
            : 0;

    const costInDollarsWithFees = exitEarly
        ? buyingCostInDollarsWithFees
        : holdsPosition
          ? exposureInDollarsWithFees
          : finalPosition !== 0
            ? finalPositionCostInDollarsWithFees
            : 0;

    const closedFeesPaid = feesPaid - positionFeeCost;
    const realizedReturnInDollars = (realizedPnl - closedFeesPaid) / 100;

    const contractsClosed = (volume - Math.abs(position) - Math.abs(finalPosition)) / 2;
    const historicalPayoutPnlInDollars =
        contractsClosed - (totalCost - positionCost - finalPositionCost - feesPaid) / 100;

    const payoutCollectedInDollars = realizedReturnInDollars + buyingCostInDollarsWithFees - exposureInDollarsWithFees;

    const averagePriceInDollars =
        finalPosition !== 0
            ? finalPositionCostInDollars / Math.abs(finalPosition)
            : position !== 0
              ? exposureInDollars / Math.abs(position)
              : Math.round((buyingCostInDollars / (volume / 2)) * 100) / 100;

    const averagePriceInDollarsWithFees =
        finalPosition !== 0
            ? finalPositionCostInDollarsWithFees / Math.abs(finalPosition)
            : position !== 0
              ? exposureInDollarsWithFees / Math.abs(position)
              : Math.round((buyingCostInDollarsWithFees / (volume / 2)) * 100) / 100;

    const potentialPayoutInDollars = Math.abs(position);

    const participatedInMarket =
        position !== 0 ||
        volume !== 0 ||
        totalCost !== 0 ||
        finalPosition !== 0 ||
        finalPositionCost !== 0 ||
        lastPosition !== 0;

    // If the market is settled, final position is the amount of contracts
    // owned; else position is. They are never non-zero at the same time.
    // Closed contracts = (volume - owned) / 2, since each was bought and sold.
    const allHistoryContractsOwned = finalPosition !== 0 ? Math.abs(finalPosition) : Math.abs(position);
    const allHistoryContractsInMarket = allHistoryContractsOwned + (volume - allHistoryContractsOwned) / 2;
    const allHistoryAveragePriceInDollars =
        allHistoryContractsInMarket === 0 ? 0 : buyingCostInDollars / allHistoryContractsInMarket;

    const baseFields: Omit<
        PortfolioPosition,
        | 'marketValueInDollars'
        | 'unrealizedReturnsInDollars'
        | 'unrealizedReturnsInPercent'
        | 'projectedMarketValueInDollars'
        | 'payoutProjectedOrCollectedInDollars'
        | 'settlementPayoutInDollars'
        | 'totalReturnsInDollars'
        | 'totalReturnsInPercent'
        | 'previousHourPriceChangeInDollars'
        | 'previousDayPriceChangeInDollars'
        | 'previousWeekPriceChangeInDollars'
    > = {
        position,
        positionFp: scalars.positionFp,
        finalPosition,
        lastPosition,
        positionCost,
        exitEarly,
        feesPaid,
        positionFeeCost,
        finalPositionCost,
        finalPositionFeeCost,
        volume,
        isSettled: scalars.isSettled,
        isClosed: scalars.isClosed,
        lastUpdatedTs: scalars.lastUpdatedTs,
        marketId: scalars.marketId,
        marketTicker: scalars.marketTicker,
        potentialPayoutInDollars,
        participatedInMarket,
        allHistoryContractsInMarket,
        allHistoryAveragePriceInDollars,
        exposureInDollars,
        exposureInDollarsWithFees,
        buyingCostInDollars,
        buyingCostInDollarsWithFees,
        finalPositionCostInDollars,
        finalPositionCostInDollarsWithFees,
        costInDollarsWithFees,
        costInDollarsWithoutFees,
        totalFeesPaidInDollars,
        realizedReturnInDollars,
        payoutCollectedInDollars,
        historicalPayoutPnlInDollars,
        averagePriceInDollars,
        averagePriceInDollarsWithFees,
    };

    return applyMarketDerivedPositionFields(baseFields, market);
};

/**
 * Recompute every `PortfolioPosition` field that depends on the live
 * `PortfolioMarket` snapshot — `marketValueInDollars`, the unrealized /
 * total return numbers, the projected/settled payouts, and the period
 * price-change deltas. Position-only scalars (cost, fees, average price,
 * realized return, etc.) on `position` are passed through untouched.
 *
 * Used for both the initial `positionVoToPortfolioPosition` build and the
 * ticker WS handler in `usePortfolioEventsStore`, so both paths produce
 * the exact same shape — the only difference is whether the position-only
 * fields originate from a fresh `PositionVo` or from the previous
 * `PortfolioPosition`.
 */
export const applyMarketDerivedPositionFields = (
    position: Omit<
        PortfolioPosition,
        | 'marketValueInDollars'
        | 'unrealizedReturnsInDollars'
        | 'unrealizedReturnsInPercent'
        | 'projectedMarketValueInDollars'
        | 'payoutProjectedOrCollectedInDollars'
        | 'settlementPayoutInDollars'
        | 'totalReturnsInDollars'
        | 'totalReturnsInPercent'
        | 'previousHourPriceChangeInDollars'
        | 'previousDayPriceChangeInDollars'
        | 'previousWeekPriceChangeInDollars'
    >,
    market: PositionMarketSnapshot,
): PortfolioPosition => {
    const isPositionYes = position.position > 0;
    const isPositionNo = position.position < 0;
    const absPosition = Math.abs(position.position);

    const lastPriceInDollars = (isPositionYes ? market.lastPrice : 100 - market.lastPrice) / 100;
    const marketValueInDollars = lastPriceInDollars > 0 ? absPosition * lastPriceInDollars : 0;

    let projectedMarketValueInDollars: number;
    if (!market.isPendingPayout) {
        projectedMarketValueInDollars = marketValueInDollars;
    } else {
        const isProjectedWin = (market.resultIsYes && isPositionYes) || (market.resultIsNo && isPositionNo);
        projectedMarketValueInDollars = isProjectedWin ? Math.max(absPosition, 0) : 0;
    }

    const projectedReturnInDollars = projectedMarketValueInDollars - position.exposureInDollarsWithFees;

    const unrealizedReturnsInDollars = market.isPendingPayout
        ? projectedReturnInDollars
        : marketValueInDollars - position.exposureInDollarsWithFees;
    const unrealizedReturnsInPercent = (() => {
        const v = unrealizedReturnsInDollars / position.exposureInDollarsWithFees;
        return isNaN(v) ? 0 : v;
    })();

    const totalReturnsInDollars = position.realizedReturnInDollars + unrealizedReturnsInDollars;
    const totalReturnsInPercent =
        position.buyingCostInDollars === 0 ? 0 : totalReturnsInDollars / position.buyingCostInDollarsWithFees;

    const isMarketClosedAndUndetermined = market.isClosed && !market.isDetermined;
    const payoutProjectedOrCollectedInDollars =
        (isMarketClosedAndUndetermined || market.isPendingPayout) && projectedMarketValueInDollars > 0
            ? projectedMarketValueInDollars
            : position.payoutCollectedInDollars > 0
              ? position.payoutCollectedInDollars
              : 0;

    let settlementPayoutInDollars: number;
    if (market.resultIsScalar) {
        const payoutPerContractInCents =
            position.finalPosition > 0 ? market.settlementValueCents : 100 - market.settlementValueCents;
        settlementPayoutInDollars = Math.abs(position.finalPosition) * (payoutPerContractInCents / 100);
    } else {
        const won =
            market.isSettled &&
            ((market.resultIsYes && position.finalPosition > 0) || (market.resultIsNo && position.finalPosition < 0));
        settlementPayoutInDollars = Math.abs(position.finalPosition) * (won ? 1 : 0);
    }

    const previousHourPriceInDollars =
        (isPositionYes ? market.previousHourPrice : 100 - market.previousHourPrice) / 100;
    const previousHourPriceChangeInDollars =
        previousHourPriceInDollars > 0 && !market.isSettled
            ? Math.abs(previousHourPriceInDollars - lastPriceInDollars)
            : 0;

    const previousDayPriceInDollars = (isPositionYes ? market.previousDayPrice : 100 - market.previousDayPrice) / 100;
    const previousDayPriceChangeInDollars =
        previousDayPriceInDollars > 0 && !market.isSettled
            ? Math.abs(previousDayPriceInDollars - lastPriceInDollars)
            : 0;

    const previousWeekPriceInDollars =
        (isPositionYes ? market.previousWeekPrice : 100 - market.previousWeekPrice) / 100;
    const previousWeekPriceChangeInDollars =
        previousWeekPriceInDollars > 0 && !market.isSettled
            ? Math.abs(previousWeekPriceInDollars - lastPriceInDollars)
            : 0;

    return {
        ...position,
        marketValueInDollars,
        unrealizedReturnsInDollars,
        unrealizedReturnsInPercent,
        projectedMarketValueInDollars,
        payoutProjectedOrCollectedInDollars,
        settlementPayoutInDollars,
        totalReturnsInDollars,
        totalReturnsInPercent,
        previousHourPriceChangeInDollars,
        previousDayPriceChangeInDollars,
        previousWeekPriceChangeInDollars,
    };
};

/**
 * Apply a `ticker` WS payload to an existing `PortfolioMarket` snapshot.
 * Mirrors the price/volume/open-interest updates that
 * `MarketStore.wsHandleTicker` applies to the live `BinaryMarket`, but
 * targets the portfolio events store's plain-object snapshot. The
 * settlement/result-related fields and the period prices are not present
 * on `TickerDTO` and pass through unchanged.
 */
export const applyTickerToPortfolioMarket = (market: PortfolioMarket, ticker: TickerDTO): PortfolioMarket => {
    const yesBid = dollarsStringToCents(ticker.yes_bid_dollars, ticker.yes_bid ?? 0);
    const yesAsk = dollarsStringToCents(ticker.yes_ask_dollars, ticker.yes_ask ?? 0);

    // Only accept a live last price that's a real traded value (1-99c) — around market
    // determination the ticker can carry a degenerate 0 or 100 (empty-book best-bid fallback)
    // that would otherwise show the position as worth $0/$1 per contract for a frame. Mirrors
    // `applyTickerToMarketEntity`'s guard (itself mirroring the removed `BinaryMarket
    // .wsHandleTicker`'s `isValidPriceDollars` check).
    const tickerLastPrice = dollarsStringToCents(ticker.price_dollars, ticker.price ?? 0);
    const lastPrice = tickerLastPrice > 0 && tickerLastPrice < 100 ? tickerLastPrice : market.lastPrice;
    const noAsk = 100 - yesBid;

    // volume_fp counts both contract legs (same as MarketVo.volume); halve to
    // get single-sided volume, matching the initial REST snapshot convention.
    // dollar_volume is already single-sided, so use it directly as fallback.
    const volume = ticker.volume_fp ? parseFloat(ticker.volume_fp) / 2 : ticker.dollar_volume;
    const openInterest = ticker.open_interest_fp
        ? parseFloat(ticker.open_interest_fp) / 2
        : ticker.dollar_open_interest;

    return {
        ...market,
        lastPrice,
        yesAsk,
        noAsk,
        yesSellPrice: yesBid,
        noSellPrice: 100 - yesAsk,
        // Mirror BinaryMarket.{yes,no}PriceInCents — `=== 100` (not `>= 100`)
        // so a sub-cent value like 99.5 still surfaces a price.
        yesPriceInCents: yesAsk === 100 ? null : yesAsk,
        noPriceInCents: noAsk === 100 ? null : noAsk,
        volume,
        openInterest,
    };
};

/**
 * Convert `EventPositionVo[]` + the matching `EventVo` map into the lean
 * `PortfolioEvent[]` shape the portfolio components consume.
 *
 * The function preserves the relative order of `eventPositions` in its
 * output — `events[n]` corresponds to the n-th VO that survived the
 * bucket filter (entries are dropped when the event has no matching
 * markets, the event metadata is missing, or the bucket criteria reject
 * every market position). Callers that need a specific top-level
 * ordering — e.g. the `/event-positions/page` route, which guarantees
 * "events in the same order as the request tickers" — should pre-sort
 * the input array and rely on this preservation.
 *
 * Per-event `markets` arrays are sorted by `subtitle` for the `open` and
 * `history` buckets to keep market rows stable across renders; the
 * top-level event order is untouched.
 */
export const toPortfolioEventsFromVos = (
    eventsMap: Map<string, EventVo>,
    eventPositions: EventPositionVo[],
    bucket: PortfolioEventBucket,
): PortfolioEvent[] => {
    const events: PortfolioEvent[] = [];
    const marketTickerToImageUrl = new Map<string, string | null>();
    for (const eventVo of eventsMap.values()) {
        // A leg market without its own rulebook image (Image URL variable) falls back to the
        // parent event's image — the same source SquareSeriesImage/getEventImageUrl use — so a
        // parlay's DoubleSquareImage leg tiles aren't left blank.
        const eventImageUrl = getEventImageUrl({
            seriesTicker: eventVo.series_ticker,
            customImageUrl: eventVo.product_metadata?.custom_image_url,
        });
        for (const m of eventVo.markets) {
            const imagePath = m.rulebook_variables?.[imageUrlKey];
            marketTickerToImageUrl.set(m.ticker_name, imagePath ? toCdnImageUrl(imagePath, 'xs') : eventImageUrl);
        }
    }
    for (const vo of eventPositions) {
        const ticker = vo.event_ticker;
        if (!ticker) continue;
        const eventVo = eventsMap.get(ticker);
        if (!eventVo) continue;

        const marketVoById = new Map(eventVo.markets.map((m) => [m.id, m]));

        const bucketPositions = vo.market_positions.filter((posVo) => {
            const positionVal = numberFromFixedPointString(posVo.position_fp);
            switch (bucket) {
                case 'open':
                    return Math.abs(positionVal) > 0 && !posVo.is_closed;
                case 'pending':
                    return (
                        Math.abs(centsFromDollarsStringOrZero(posVo.position_cost_dollars)) > 0 &&
                        posVo.is_closed &&
                        !posVo.is_settled
                    );
                case 'history':
                    return Math.abs(positionVal) === 0;
            }
        });

        if (!bucketPositions.length) continue;

        const markets: PortfolioMarketPosition[] = bucketPositions.flatMap((posVo) => {
            const marketVo = marketVoById.get(posVo.market_id);
            if (!marketVo) return [];
            const market = marketVoToPortfolioMarket(marketVo, eventVo);
            const position = positionVoToPortfolioPosition(posVo, market);
            return [{ market, position }];
        });

        if (bucket === 'open' || bucket === 'history') {
            markets.sort((a, b) => a.market.subtitle.localeCompare(b.market.subtitle));
        }

        if (!markets.length) continue;

        const firstMarketAssociated = eventVo.markets[0]?.rulebook_variables?.['Associated Markets'];
        const associatedMarketTickers = parseAssociatedMarketTickers(firstMarketAssociated);
        const seriesTicker = eventVo.series_ticker;
        const relativeUrl = getEventRelativeUrl(seriesTicker, undefined, ticker);
        const openDate =
            eventVo.markets
                .map((m) => m.open_date)
                .filter(Boolean)
                .sort()[0] ?? '';

        events.push({
            bucket,
            eventTicker: ticker,
            seriesTicker,
            feeTypeOverride: eventVo.fee_type_override,
            feeMultiplierOverride: eventVo.fee_multiplier_override,
            title: eventVo.title.replace(/\*\*/g, ''),
            absoluteUrl: `${config.baseUrl}${relativeUrl}`,
            relativeUrl,
            openDate,
            closeDate: eventVo.close_date ?? '',
            category: parseBECategoryToCategory(eventVo.category),
            competition: eventVo.product_metadata?.competition,
            topTitle: eventVo.product_metadata_derived?.general_title_super || undefined,
            isParlay: associatedMarketTickers.length > 1,
            associatedMarketTickers,
            associatedMarketImageUrls: associatedMarketTickers.map((t) => marketTickerToImageUrl.get(t) ?? ''),
            collateralReturn: vo.collateral_returned_cents,
            lastUpdatedTs: vo.last_updated_ts,
            markets,
        });
    }
    return events;
};
